import { useCallback, useEffect, useMemo, useState, useContext } from "react";

import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { AlertConfirmDeleteMeasurement } from "@/shared/notifications/AlertConfirm";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	CloseActionButton,
	DeleteActionButton,
	SaveActionButton,
} from "@/shared/ui";
import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
	useDeleteLabMeasurement,
	useUpdateMeasurement,
} from "@/service/LabHomeMutations";
import { useFetchLabParams } from "@/service/LabHomeQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);

/**
 * LabMeasurementDialog component is a dialog used for editing or creating lab measurements.
 * It provides a form to input various details about a lab measurement, such as tag, parameter, measurement value, test date, comment, and export option.
 * The component also supports saving and deleting the measurement data.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {boolean} props.open - Determines whether the dialog is open or closed.
 * @param {function} props.onClose - Function to call when the dialog is closed.
 * @param {number} props.measurementId - The ID of the measurement being edited.
 * @param {Object} props.LabItem - The lab item data used to populate the form when editing.
 *
 * @returns {JSX.Element} The LabMeasurementDialog component.
 *
 * @example
 * <LabMeasurementDialog
 *   open={isOpen}
 *   onClose={handleClose}
 *   measurementId={selectedMeasurementId}
 *   LabItem={selectedLabItem}
 * />
 *
 * @remarks
 * This component uses several hooks for state management and data fetching:
 * - `useTime` to get the current timezone.
 * - `useState` to manage the form data and parameter state.
 * - `useUpdateMeasurement` to handle the update mutation.
 * - `useDeleteLabMeasurement` to handle the delete mutation.
 * - `useFetchLabParams` to fetch the available lab parameters.
 *
 */
interface SpotSampleDialogProps {
	open: boolean;
	onClose: () => void;
	measurementId: number;
	LabItem: SpotSampleLabItem | null;
}

interface SpotSampleLabItem {
	LabMeasurementId?: number | null;
	LabAnalyzeId?: number | null;
	GroupId?: number | null;
	TagId?: string | null;
	Measurement?: number | null;
	TestDate?: string | null;
	Comment?: string | null;
	Parameter?: number | null;
	ExportToPi?: boolean | null;
	AnalyseType?: string | null;
}

interface SpotSampleMeasurementData {
	labMeasurementId: number;
	labAnalyzeId: number;
	groupId: number;
	tagId: string;
	measurement: number | null;
	testDate: string;
	comment: string;
	parameter: number;
	exportToPi: boolean;
	analyseType: string;
}

interface LabParameterOption {
	ParameterId: number;
	Parameter: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const toNumber = (value: unknown, fallback = 0): number => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: unknown): number | null => {
	if (value === null || value === undefined || value === "") return null;
	if (typeof value === "number" && Number.isFinite(value)) return value;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const toStringValue = (value: unknown, fallback = ""): string =>
	typeof value === "string" ? value : fallback;

const getErrorMessage = (error: unknown, fallback: string): string => {
	if (isRecord(error)) {
		const response = isRecord(error.response) ? error.response : undefined;
		const data =
			response && isRecord(response.data) ? response.data : undefined;
		const responseMessage = data ? data.message : undefined;
		if (typeof responseMessage === "string" && responseMessage.trim()) {
			return responseMessage;
		}

		const message = error.message;
		if (typeof message === "string" && message.trim()) {
			return message;
		}
	}

	if (error instanceof Error && error.message.trim()) {
		return error.message;
	}

	return fallback;
};

const toLabParameterOption = (value: unknown): LabParameterOption | null => {
	if (!isRecord(value)) {
		return null;
	}

	return {
		ParameterId: toNumber(value.ParameterId),
		Parameter: toStringValue(value.Parameter),
	};
};

const getDialogFieldSx = (theme: ReturnType<typeof useTheme>) => ({
	"& .MuiOutlinedInput-root": {
		borderRadius: 2,
		backgroundColor: theme.palette.background.paper,
		"& fieldset": {
			borderColor: alpha(theme.palette.divider, 0.82),
		},
		"&:hover fieldset": {
			borderColor: theme.palette.text.disabled,
		},
		"&.Mui-focused fieldset": {
			borderColor: theme.palette.primary.main,
			borderWidth: 1,
		},
	},
	"& .MuiInputLabel-root": {
		fontWeight: 500,
	},
});

export const SpotSampleDialog = ({
	open,
	onClose,
	measurementId,
	LabItem,
}: SpotSampleDialogProps) => {
	const translation: Language | null = useContext(TranslationContext);
	const { timezone } = useTime();
	const theme = useTheme();

	const [measurementData, setMeasurementData] =
		useState<SpotSampleMeasurementData>(() => ({
			labMeasurementId: 0,
			labAnalyzeId: 0,
			groupId: 0,
			tagId: "",
			measurement: null,
			testDate: dayjs.utc().tz(timezone).toISOString(),
			comment: "",
			parameter: 0,
			exportToPi: false,
			analyseType: "Default",
		}));
	const updateMeasurementMutation = useUpdateMeasurement();
	const deleteMutation = useDeleteLabMeasurement();
	const isSavingMeasurement = updateMeasurementMutation.isPending;
	const isDeletingMeasurement = deleteMutation.isPending;
	const isActionPending = isSavingMeasurement || isDeletingMeasurement;
	const {
		data: labParamsData,
		isLoading: paramsLoading,
		isError: paramsError,
	} = useFetchLabParams();

	const labParams = useMemo(
		() =>
			Array.isArray(labParamsData)
				? labParamsData
						.map(toLabParameterOption)
						.filter((param): param is LabParameterOption => param !== null)
				: [],
		[labParamsData],
	);

	const testDateInputValue = useMemo(
		() =>
			dayjs
				.utc(measurementData.testDate)
				.tz(timezone)
				.format("YYYY-MM-DDTHH:mm"),
		[measurementData.testDate, timezone],
	);

	// Populating the form if editing and LabItem is available
	useEffect(() => {
		if (LabItem) {
			setMeasurementData({
				labMeasurementId: toNumber(LabItem.LabMeasurementId),
				labAnalyzeId: toNumber(LabItem.LabAnalyzeId),
				groupId: toNumber(LabItem.GroupId),
				tagId: toStringValue(LabItem.TagId),
				measurement: toNullableNumber(LabItem.Measurement),
				testDate: LabItem.TestDate
					? dayjs.utc(LabItem.TestDate).tz(timezone).toISOString()
					: dayjs.utc().tz(timezone).toISOString(),
				comment: toStringValue(LabItem.Comment),
				parameter: toNumber(LabItem.Parameter),
				exportToPi: Boolean(LabItem.ExportToPi),
				analyseType: toStringValue(LabItem.AnalyseType, "Default"),
			});
		}
	}, [LabItem, measurementId, timezone]);

	const handleSave = useCallback(async () => {
		if (isActionPending) {
			return;
		}

		try {
			await updateMeasurementMutation.mutateAsync(measurementData);
			void AlertToast.success("Measurement saved successfully");
			onClose();
		} catch (error: unknown) {
			void AlertToast.failed(
				getErrorMessage(error, "Failed to save measurement"),
			);
			console.error("Error saving measurement:", error);
		}
	}, [isActionPending, measurementData, onClose, updateMeasurementMutation]);

	const handleDelete = useCallback(async () => {
		if (isActionPending) {
			return;
		}

		try {
			await deleteMutation.mutateAsync(measurementData.labMeasurementId);
			void AlertToast.success("Measurement deleted successfully");
			onClose();
		} catch (error: unknown) {
			void AlertToast.failed(
				getErrorMessage(error, "Failed to delete measurement"),
			);
			console.error("Error deleting measurement:", error);
		}
	}, [
		deleteMutation,
		isActionPending,
		measurementData.labMeasurementId,
		onClose,
	]);

	const confirmDelete = useCallback(() => {
		if (isActionPending) {
			return;
		}

		void AlertConfirmDeleteMeasurement(() => {
			void handleDelete();
		}, theme.palette.mode === "light");
	}, [handleDelete, isActionPending, theme.palette.mode]);

	const handleSaveClick = useCallback(() => {
		void handleSave();
	}, [handleSave]);

	const handleTagIdChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setMeasurementData((prevData) => ({
				...prevData,
				tagId: event.target.value,
			}));
		},
		[],
	);

	const handleParameterChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setMeasurementData((prevData) => ({
				...prevData,
				parameter: toNumber(event.target.value),
			}));
		},
		[],
	);

	const handleMeasurementChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setMeasurementData((prevData) => ({
				...prevData,
				measurement: toNumber(event.target.value),
			}));
		},
		[],
	);

	const handleTestDateChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const date = dayjs.utc(event.target.value).tz(timezone);
			if (date.isValid()) {
				setMeasurementData((prevData) => ({
					...prevData,
					testDate: date.toISOString(),
				}));
			}
		},
		[timezone],
	);

	const handleCommentChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setMeasurementData((prevData) => ({
				...prevData,
				comment: event.target.value,
			}));
		},
		[],
	);

	const handleExportToPiChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setMeasurementData((prevData) => ({
				...prevData,
				exportToPi: event.target.checked,
			}));
		},
		[],
	);

	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			open={open}
			onClose={isActionPending ? undefined : onClose}
			maxWidth="sm"
			fullWidth
			aria-labelledby="spot-sample-dialog-title"
			PaperProps={{
				sx: {
					borderRadius: 2,
					border: "1px solid",
					borderColor: "divider",
					overflow: "hidden",
					bgcolor: "background.paper",
				},
			}}
		>
				<DialogTitle
				id="spot-sample-dialog-title"
				component="div"
				sx={{
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
					borderBottom: 1,
					borderColor: "divider",
					bgcolor: "background.default",
				}}
				>
					<Box sx={{ pr: 5 }}>
						<Typography
							variant="overline"
							sx={{
								display: "block",
								letterSpacing: "0.08em",
								fontWeight: 800,
								color: "primary.main",
								lineHeight: 1.2,
								mb: 0.25,
							}}
						>
							<Translate String="Auto.Features.Lab.Ui.Components.SpotSampleDialog.L422C8.OtherSamples">Other samples</Translate>
						</Typography>
						<Typography
							variant="h6"
							component="h2"
							sx={{ fontWeight: 700, color: "text.primary" }}
						>
							<Translate String="SpotSampleDialog.Header">
								Edit Spot Measurement
							</Translate>
						</Typography>
					</Box>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L436C17.Close", "Close")}
					onClick={onClose}
					size="small"
					disabled={isActionPending}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						...closeButtonSx,
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					bgcolor: "background.paper",
				}}
			>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L462C14.Tag", "Tag")}
							name="tagId"
							variant="outlined"
							value={measurementData.tagId}
							onChange={handleTagIdChange}
							fullWidth
							sx={getDialogFieldSx(theme)}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L474C14.Parameter", "Parameter")}
							variant="outlined"
							select
							value={String(measurementData.parameter)}
							onChange={handleParameterChange}
							fullWidth
							disabled={paramsLoading || paramsError}
							sx={getDialogFieldSx(theme)}
						>
							<MenuItem value=""><Translate String="Auto.Features.Lab.Ui.Components.SpotSampleDialog.L483C27.SelectParameter">Select parameter</Translate></MenuItem>
							{labParams.map((param) => (
								<MenuItem key={param.ParameterId} value={param.ParameterId}>
									{param.Parameter}
								</MenuItem>
							))}
						</TextField>
					</Grid>

					<Grid item xs={12}>
						<TextField
							label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L494C14.MeasurementValue", "Measurement Value")}
							name="measurement"
							variant="outlined"
							value={measurementData.measurement}
							onChange={handleMeasurementChange}
							fullWidth
							type="number"
							sx={getDialogFieldSx(theme)}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L507C14.TestDate", "Test Date")}
							name="testDate"
							type="datetime-local"
							variant="outlined"
							value={testDateInputValue}
							onChange={handleTestDateChange}
							fullWidth
							InputLabelProps={{ shrink: true }}
							sx={getDialogFieldSx(theme)}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L521C14.Comment", "Comment")}
							name="comment"
							variant="outlined"
							value={measurementData.comment}
							onChange={handleCommentChange}
							fullWidth
							multiline
							rows={2}
							sx={getDialogFieldSx(theme)}
						/>
					</Grid>

					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Checkbox
									checked={measurementData.exportToPi}
									onChange={handleExportToPiChange}
									name="exportToPi"
								/>
							}
							label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L542C14.ExportToPi", "Export to Pi")}
						/>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions
				sx={{
					px: { xs: 2, sm: 2.5 },
					py: 2,
					borderTop: 1,
					borderColor: "divider",
					bgcolor: "background.default",
					display: "flex",
					justifyContent: "flex-end",
					gap: 1.5,
					flexWrap: "wrap",
				}}
			>
				<CloseActionButton
					onClick={onClose}
					disabled={isActionPending}
				>
					<Translate String="Procedure.Button.Close">Close</Translate>
				</CloseActionButton>
				<DeleteActionButton
					onClick={confirmDelete}
					loading={isDeletingMeasurement}
					loadingText={
						<Translate String="LabDaily.Deleting">Deleting...</Translate>
					}
					disabled={isActionPending || measurementData.labMeasurementId <= 0}
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L574C17.DeleteMeasurement", "Delete measurement")}
				>
					<Translate String="Shift.Event.Delete">Delete</Translate>
				</DeleteActionButton>
				<SaveActionButton
					onClick={handleSaveClick}
					loading={isSavingMeasurement}
					loadingText={
						<Translate String="LabDaily.Saving">Saving...</Translate>
					}
					disabled={isActionPending}
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.SpotSampleDialog.L585C17.SaveMeasurement", "Save measurement")}
				>
					<Translate String="Tasks.Task.Comment.Save">Save</Translate>
				</SaveActionButton>
			</DialogActions>
		</Dialog>
	);
};

export default SpotSampleDialog;
